import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, FileText, Trash2, Download, MapPin, Save, Scale, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import TrustedProviders from '../components/TrustedProviders';
import SecurityVerification, { useSecurityStatus, isSecurityVerified } from '../components/SecurityVerification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WillUpload() {
  const navigate = useNavigate();
  const [will, setWill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [physicalLocation, setPhysicalLocation] = useState('');
  const [savingLocation, setSavingLocation] = useState(false);
  
  // Security verification state
  const [securityVerificationOpen, setSecurityVerificationOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { hasPin } = useSecurityStatus();

  useEffect(() => {
    loadWill();
    loadPhysicalLocation();
  }, []);

  const loadWill = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/will`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWill(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to load will:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPhysicalLocation = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/will/physical-location`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhysicalLocation(response.data.physical_location || '');
    } catch (error) {
      // Location not set yet, that's okay
      console.log('Physical location not set');
    }
  };

  const savePhysicalLocation = async () => {
    const token = localStorage.getItem('session_token');
    setSavingLocation(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/will/physical-location`,
        { physical_location: physicalLocation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Physical location saved');
    } catch (error) {
      console.error('Failed to save location:', error);
      toast.error('Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('session_token');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/will/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setWill(response.data);
      toast.success('Will uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload will');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    // Check if security verification is required
    if (hasPin && !isSecurityVerified()) {
      setPendingAction('delete');
      setSecurityVerificationOpen(true);
      return;
    }
    
    performDelete();
  };

  const performDelete = async () => {
    const token = localStorage.getItem('session_token');

    try {
      await axios.delete(`${BACKEND_URL}/api/will`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWill(null);
      toast.success('Will deleted');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete will');
    }
  };

  const handleSecurityVerified = () => {
    setSecurityVerificationOpen(false);
    if (pendingAction === 'delete') {
      performDelete();
    }
    setPendingAction(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B4C7E] border-r-transparent mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]" data-testid="will-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent truncate">Last Will & Testament</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-8">
        {!will ? (
          <>
            <Card className="p-12 text-center rounded-2xl border border-[#EBF0F5] bg-white/90 backdrop-blur-md shadow-lg">
              <div className="w-24 h-24 bg-gradient-to-br from-[#EBF0F5] to-[#D7E0EA] rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-12 w-12 text-[#2B4C7E]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-medium text-gray-800 mb-4">Upload Your Will</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Securely store a copy of your Last Will & Testament. This will be made available to your designated primary beneficiary.
              </p>
              <label htmlFor="will-upload" className="inline-block">
                <Button
                  disabled={uploading}
                  className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full px-8 py-6 hover:from-[#1E3A63] hover:to-[#1E3A63]"
                  onClick={() => document.getElementById('will-upload').click()}
                  data-testid="upload-will-btn"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Will'}
                </Button>
              </label>
              <input
                id="will-upload"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleUpload}
                className="hidden"
                data-testid="will-file-input"
              />
              <p className="text-sm text-gray-500 mt-4">Accepted formats: PDF, DOC, DOCX</p>
            </Card>

            {/* Trusted Legal Services Section - Show when no will uploaded */}
            <motion.div 
              className="mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <TrustedProviders 
                category="legal"
                title="Need Help Creating a Will?"
                description="Browse trusted estate planning services and attorneys to create your legal documents"
                icon={Scale}
              />
            </motion.div>
          </>
        ) : (
          <Card className="p-8 rounded-2xl border border-[#EBF0F5] bg-white/90 backdrop-blur-md shadow-lg">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="h-8 w-8 text-green-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-medium text-gray-800 mb-2">Will Uploaded</h3>
                <p className="text-gray-600 mb-4">{will.document_name}</p>
                <div className="space-y-2 text-sm text-gray-600 mb-6">
                  <p>File Size: {formatFileSize(will.file_size)}</p>
                  <p>Uploaded: {new Date(will.uploaded_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => window.open(will.document_url, '_blank')}
                    variant="outline"
                    className="rounded-full"
                    data-testid="view-will-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    View/Download
                  </Button>
                  <label htmlFor="will-reupload">
                    <Button
                      disabled={uploading}
                      variant="outline"
                      className="rounded-full"
                      onClick={() => document.getElementById('will-reupload').click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Replace'}
                    </Button>
                  </label>
                  <input
                    id="will-reupload"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={handleDelete}
                    variant="outline"
                    className="rounded-full text-red-600 hover:bg-red-50"
                    data-testid="delete-will-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-[#EBF0F5] rounded-xl">
              <p className="text-sm text-[#182E4F]">
                <strong>Note:</strong> This document will be accessible to your primary beneficiary after the Easy Passing process is triggered.
              </p>
            </div>
          </Card>
        )}

        {/* Information Card */}
        <Card className="mt-8 p-6 rounded-2xl border border-[#EBF0F5] bg-gradient-to-br from-[#EBF0F5] to-white">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Important Information</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Your will is securely encrypted and stored</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Only your designated primary beneficiary will have access after your passing</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>This should be a copy - keep your original will in a safe place</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span>Update this document whenever you make changes to your will</span>
            </li>
          </ul>
        </Card>

        {/* Physical Location of Original Will */}
        <Card className="mt-8 p-6 rounded-2xl border border-[#EAD9BF] bg-gradient-to-br from-[#F7F1E8] to-[#F7F1E8] shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D0B384] to-[#C8A97E] rounded-xl flex items-center justify-center">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">Physical Will Location</h3>
              <p className="text-sm text-gray-600">Where is the original physical copy of your will stored?</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Details
              </label>
              <Input
                value={physicalLocation}
                onChange={(e) => setPhysicalLocation(e.target.value)}
                placeholder="e.g., Safe deposit box at First National Bank, Box #1234, Downtown Branch"
                className="rounded-xl border-2 border-[#EAD9BF] focus:border-[#D0B384] bg-white"
                data-testid="physical-location-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                Include specific details like bank name, box number, safe combination hints, or attorney contact info.
              </p>
            </div>
            
            <Button
              onClick={savePhysicalLocation}
              disabled={savingLocation}
              className="bg-gradient-to-r from-[#C8A97E] to-[#C8A97E] text-white rounded-full hover:from-[#B3956B] hover:to-[#B3956B]"
              data-testid="save-location-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingLocation ? 'Saving...' : 'Save Location'}
            </Button>
          </div>
          
          <div className="mt-4 p-3 bg-[#F2E9D9]/50 rounded-xl">
            <p className="text-sm text-[#836A45]">
              <strong>Tip:</strong> This information will be shared with your primary beneficiary to help them locate your original will.
            </p>
          </div>
        </Card>
      </main>

      {/* Security Verification Dialog */}
      <SecurityVerification
        open={securityVerificationOpen}
        onClose={() => {
          setSecurityVerificationOpen(false);
          setPendingAction(null);
        }}
        onVerified={handleSecurityVerified}
        title="Verify to Delete Will"
        description="Enter your security PIN to delete your will"
      />
    </div>
  );
}